import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import {
  ClipboardList,
  ExternalLink,
  FileText,
  Loader2,
  PackageCheck,
  Pencil,
  Receipt,
  Share2,
  Trash2,
  Truck,
  Undo2,
} from 'lucide-react';
import { format } from 'date-fns';

import PageHeader from '../components/PageHeader.jsx';
import RevertSaleSheet from '../components/RevertSaleSheet.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { deleteSale, deliverSale, getSale, getSalePdfBlob } from '../api/sales';
import { createReleaseNote } from '../api/releaseNotes';
import { canShareFiles, sharePdfBlob } from '../utils/share';

/**
 * Sale detail — shows everything needed to verify a sale and send the
 * proforma to a customer. Lands here both:
 *   (a) immediately after creating a sale (NewSale → /sales/:id), and
 *   (b) by tapping a row on the Sales list page.
 *
 * If we arrive with a `flash` message in router state (e.g. "Sale saved"),
 * surface it briefly at the top.
 */

// Must match backend enum (backend/app/models/sale.py): draft | delivered |
// paid | write_off | cancelled. Same colours as Sales.jsx so the same row
// looks identical in list and detail views.
const STATUS_PILL = {
  draft:      'bg-gray-100 text-gray-700',
  delivered:  'bg-green-100 text-green-700',
  paid:       'bg-green-100 text-green-800',
  write_off:  'bg-orange-100 text-orange-800',
  cancelled:  'bg-red-100 text-red-700',
};

function StatusPill({ status }) {
  const cls = STATUS_PILL[status] ?? 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-pill text-caption font-medium capitalize ${cls}`}>
      {status?.replace(/_/g, ' ') ?? 'unknown'}
    </span>
  );
}

function fmt(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'd MMM yyyy');
  } catch {
    return '—';
  }
}

export default function SaleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();
  const flash = location.state?.flash;

  // Revert sheet visibility — separate from `transition` because the
  // sheet has its own internal submitting state and we want the rep to
  // be able to back out without trapping the page in a transition state.
  const [revertOpen, setRevertOpen] = useState(false);

  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per-PDF loading state so the user gets feedback while the proforma /
  // invoice is being fetched + converted to a blob URL.
  const [pdfLoading, setPdfLoading] = useState(null); // 'proforma' | 'invoice' | null
  const [pdfError, setPdfError] = useState(null);

  // Per-PDF share state — independent of pdfLoading so a share-in-flight
  // doesn't block the rep from also tapping View, and vice-versa.
  const [pdfSharing, setPdfSharing] = useState(null); // 'proforma' | 'invoice' | null
  const shareSupported = canShareFiles();

  // Release note attached to this sale (at most one per sale, enforced
  // by the backend with a 409). null = no RN exists yet. Otherwise a
  // small summary { id, release_number, status } so the actions
  // section can render a deeplink with status. Derived from the sale
  // response — see the useMemo below.
  const [rnError, setRnError] = useState(null);

  // Lifecycle transition state — single in-flight transition gate
  // disables ALL transition buttons so the user can't double-tap across
  // them. Mirrors QuoteDetail's pattern.
  const [transition, setTransition] = useState(null); // 'create_rn' | 'deliver' | 'delete' | null
  const [transitionError, setTransitionError] = useState(null);
  const [transitionFlash, setTransitionFlash] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRnError(null);
    // Drop any stale RN-from-create-action state when navigating to
    // a different sale (e.g. via an in-app link).
    setRnFromCreate(null);

    getSale(id)
      .then((data) => { if (!cancelled) setSale(data); })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.detail || err.message || 'Failed to load sale');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [id]);

  // Local override that wins over `sale.release_note_*` after the
  // rep generates an RN on this page — `setSale` returns the same
  // sale object from the cache; the new RN ID/status only landed in
  // our local state. Cleared on navigation.
  const [rnFromCreate, setRnFromCreate] = useState(null);

  // Release-note summary used by the actions section. Derived from
  // SaleOut.release_note_id / release_note_status (which the backend
  // returns alongside the sale — saves a second API round-trip on
  // every detail-page open). Falls through to the local override
  // for the just-created case.
  const rnSummary = useMemo(() => {
    if (rnFromCreate) return rnFromCreate;
    if (!sale || !sale.release_note_id) return null;
    return {
      id:             sale.release_note_id,
      release_number: sale.release_note_number ?? `RN-${sale.release_note_id}`,
      status:         sale.release_note_status,
    };
  }, [sale, rnFromCreate]);

  if (loading) {
    return (
      <main className="min-h-full px-4 pb-6">
        <PageHeader title="Sale" />
        <p className="text-small text-gray-500">Loading…</p>
      </main>
    );
  }

  if (error || !sale) {
    return (
      <main className="min-h-full px-4 pb-6">
        <PageHeader title="Sale" />
        <p role="alert" className="text-small text-danger">
          {error || 'Sale not found.'}
        </p>
      </main>
    );
  }

  /**
   * Fetch the PDF as a blob (auth required; can't just open the URL in a new
   * tab because the new tab wouldn't carry our Bearer token), wrap it in an
   * object URL, and trigger an anchor click so the browser shows it inline.
   * Object URL is revoked after a minute — long enough for the new tab to
   * have loaded the bytes.
   */
  async function openPdf(type) {
    setPdfLoading(type);
    setPdfError(null);
    try {
      const blob = await getSalePdfBlob(sale.id, type);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after 5s — any modern browser has fetched the blob's
      // bytes into the new tab by then, even iOS Safari which can
      // lazy-load background tabs. 60s (the previous value) was
      // overcautious and left blob bytes pinned for far longer than
      // the rep needed.
      setTimeout(() => URL.revokeObjectURL(url), 5_000);
    } catch (err) {
      setPdfError(
        err.response?.data?.detail || err.message || `Failed to load ${type} PDF`,
      );
    } finally {
      setPdfLoading(null);
    }
  }

  /**
   * Share a PDF (proforma or invoice) via the native OS share sheet.
   *
   * Fetches the bytes via the same authenticated apiClient used by
   * openPdf, then hands off to the Web Share API helper. Outcomes:
   *
   *   shared      — share sheet completed; no toast needed (the rep
   *                 just saw the share sheet and acted)
   *   cancelled   — user dismissed the sheet; silent (not an error)
   *   unsupported — shouldn't happen here because the share button
   *                 is only rendered when canShareFiles() is true,
   *                 but we surface it as an inline error just in case
   *   error       — propagate detail inline
   */
  async function onSharePdf(type) {
    setPdfSharing(type);
    setPdfError(null);
    try {
      const blob = await getSalePdfBlob(sale.id, type);
      const filename = `${type}-${sale.invoice_number ?? sale.id}.pdf`;
      const result = await sharePdfBlob(blob, filename, {
        title: `${type === 'invoice' ? 'Invoice' : 'Proforma'} ${sale.invoice_number ?? `#${sale.id}`}`,
        text:  sale.customer_name
          ? `${type === 'invoice' ? 'Invoice' : 'Proforma'} for ${sale.customer_name}`
          : undefined,
      });
      if (result.outcome === 'error') {
        setPdfError(result.error?.message || 'Failed to share PDF');
      } else if (result.outcome === 'unsupported') {
        setPdfError('Sharing isn’t supported on this device.');
      }
    } catch (err) {
      setPdfError(
        err.response?.data?.detail || err.message || `Failed to share ${type}`,
      );
    } finally {
      setPdfSharing(null);
    }
  }

  /**
   * Generate a release note for this sale.
   *
   * Backend takes care of populating items from the sale's line items
   * (one ReleaseNoteItem per SaleLineItem). On success the response is
   * the freshly-created ReleaseNoteOut (status="created"); we adapt it
   * into the lighter list-shape we already store in `rnSummary` so the
   * actions section instantly re-renders to show the Open-RN row, and
   * the rep can continue straight into the picking flow.
   */
  async function onCreateRn() {
    const ok = await confirm({
      title:        'Generate release note',
      message:      'Create a release note for this sale? It will start in the “created” state ready for picking.',
      confirmLabel: 'Generate',
    });
    if (!ok) return;

    setTransition('create_rn');
    setTransitionError(null);
    setTransitionFlash(null);
    try {
      const created = await createReleaseNote(sale.id);
      // Stash the freshly-created RN's id/number/status in a local
      // override — the sale object we already hold doesn't know
      // about it yet (no refetch), and the rnSummary memo prefers
      // this override when it's set.
      setRnFromCreate({
        id:             created.id,
        release_number: created.release_number,
        status:         created.status,
      });
      setTransitionFlash(`Release note ${created.release_number} created · open it to begin picking`);
    } catch (err) {
      setTransitionError(
        err.response?.data?.detail || err.message || 'Failed to create release note',
      );
    } finally {
      setTransition(null);
    }
  }

  /**
   * Mark this draft sale as delivered.
   *
   * Backend `POST /sales/{id}/deliver` (require_admin_or_sales): no
   * body, deducts stock FIFO, creates the customer ledger debit, leaves
   * fiscalisation alone (separate desktop+2FA flow). Returns the full
   * updated SaleOut so we can drop it straight into local state.
   *
   * Soft pre-condition we surface to the rep: the release note should
   * be `collected` first. The backend doesn't enforce the linkage
   * (delivery and RN are parallel state machines) but the in-the-field
   * mental model is "release the goods, then mark delivered." If a
   * non-collected RN exists we show a confirm-with-warning so the rep
   * can override deliberately if needed.
   */
  async function onMarkDelivered() {
    const rnNotCollected = rnSummary && rnSummary.status !== 'collected';
    const ok = await confirm({
      title:        'Mark as delivered',
      message: rnNotCollected
        ? `The release note (${rnSummary.release_number}) is still in “${rnSummary.status}”. Mark this sale as delivered anyway?\n\nDelivering will deduct stock FIFO and post the customer ledger entry. Fiscalisation happens separately on desktop.`
        : 'Mark this sale as delivered? Stock will be deducted FIFO and the customer ledger entry posted. Fiscalisation happens separately on desktop.',
      confirmLabel: rnNotCollected ? 'Deliver anyway' : 'Mark delivered',
      confirmVariant: rnNotCollected ? 'danger' : 'success',
    });
    if (!ok) return;

    setTransition('deliver');
    setTransitionError(null);
    setTransitionFlash(null);
    try {
      const updated = await deliverSale(sale.id);
      setSale(updated);
      setTransitionFlash('Sale marked delivered · stock deducted, ledger entry posted');
    } catch (err) {
      setTransitionError(
        err.response?.data?.detail || err.message || 'Failed to mark sale delivered',
      );
    } finally {
      setTransition(null);
    }
  }

  /**
   * Hard-delete this draft sale.
   *
   * Backend `DELETE /sales/{id}` (require_admin_or_sales): releases
   * any reserved on-hold stock back to available, then cascade-deletes
   * line items + expected payments. Only works on draft. After
   * success there's nothing left to render here, so navigate to the
   * sales list and surface a flash so the rep knows the action took.
   *
   * Confirmed via the standard ConfirmDialog (danger variant) — the
   * action is destructive but recoverable in the sense that nothing
   * else was happening (no payments, no fiscal entries).
   */
  async function onDelete() {
    const ok = await confirm({
      title:        'Delete sale',
      message:      `Delete ${sale.invoice_number ?? `Sale #${sale.id}`}?\n\nReserved stock will be released back to available. This action cannot be undone.`,
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
    });
    if (!ok) return;

    setTransition('delete');
    setTransitionError(null);
    setTransitionFlash(null);
    try {
      await deleteSale(sale.id);
      navigate('/sales', {
        replace: true,
        state: {
          flash: `${sale.invoice_number ?? `Sale #${sale.id}`} deleted · stock released`,
        },
      });
    } catch (err) {
      setTransitionError(
        err.response?.data?.detail || err.message || 'Failed to delete sale',
      );
      setTransition(null);
    }
    // Note: no setTransition(null) on success — the page is unmounting.
  }

  // Invoice only makes sense once the sale has progressed past draft.
  // Backend produces a meaningful invoice for delivered/paid/write_off; for
  // draft and cancelled the doc is empty or misleading.
  const showInvoice = ['delivered', 'paid', 'write_off'].includes(sale.status);

  // Action gating. Most actions are draft-only and hide once the sale
  // moves on. Revert is the inverse (delivered/paid + non-fiscalised
  // only). RN creation is the lone exception: backend doesn't gate it
  // by sale status, so we allow it everywhere except cancelled — that
  // way reps who delivered before generating an RN can still recover
  // from mobile rather than having to revert → generate → re-deliver.
  const canCreateRn      = rnSummary === null && sale.status !== 'cancelled';
  const canMarkDelivered = sale.status === 'draft';
  const canDelete        = sale.status === 'draft';
  const canRevert        =
    ['delivered', 'paid'].includes(sale.status) && !sale.fiscal_number;
  // When the sale IS fiscalised AND in a reverted-eligible state, surface
  // a hint so the rep knows the Credit Note path exists rather than
  // silently hiding the option.
  const showFiscalisedHint =
    ['delivered', 'paid'].includes(sale.status) && Boolean(sale.fiscal_number);
  const transitionInFlight = transition !== null;

  return (
    <main className="min-h-full px-4 pb-6">
      {/* Back uses PageHeader's default navigate(-1) — proper browser back.
          NewSale → SaleDetail uses navigate(replace) so back from a freshly
          created sale lands on Home; from the Sales list back lands on the
          list. Forcing navigate('/sales') here pushed a new history entry
          and produced a back/forward loop between detail and list. */}
      <PageHeader
        title={sale.invoice_number ?? `Sale #${sale.id}`}
        subtitle={sale.customer_name ?? '—'}
        icon={Receipt}
        right={canDelete ? (
          // Header-right Delete affordance — only meaningful while the
          // sale is draft (backend gates DELETE to draft). White icon
          // matches the back chevron's tint so the header stays
          // visually unified; the destructive framing happens in the
          // ConfirmDialog rather than via colour. Swaps to a spinner
          // while the delete request is in flight to disable
          // double-taps and reassure the rep something's happening.
          <button
            type="button"
            onClick={onDelete}
            disabled={transitionInFlight}
            aria-label="Delete sale"
            className="-mr-2 min-h-touch min-w-touch flex items-center justify-center text-white/90 active:text-white disabled:opacity-60"
          >
            {transition === 'delete' ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Trash2 className="h-5 w-5" />
            )}
          </button>
        ) : null}
      />

      {flash && (
        <div role="status" className="mb-3 mb-card bg-green-50 border-green-200 text-small text-green-900">
          {flash}
        </div>
      )}

      {/* Status + date summary card */}
      <section className="mb-card mb-3 flex items-center justify-between">
        <div>
          <p className="text-caption text-gray-500">Status</p>
          <div className="mt-1"><StatusPill status={sale.status} /></div>
        </div>
        <div className="text-right">
          <p className="text-caption text-gray-500">Date</p>
          <p className="mt-1 text-body font-medium text-gray-900">{fmtDate(sale.date)}</p>
        </div>
      </section>

      {/* Line items */}
      <section className="mb-card mb-3">
        <div className="flex items-center justify-between mb-2">
          <p className="text-small font-medium text-gray-700">Line items</p>
          {/* Inline edit affordance — only meaningful while the sale is
              draft (backend gates PATCH to draft). Renders as a small
              icon-only button so it doesn't compete with the line-item
              listing for visual weight. The earlier full-width "Edit
              line items" action row was overkill for a contextual
              edit. */}
          {sale.status === 'draft' && (
            <Link
              to={`/sales/${sale.id}/edit`}
              aria-label="Edit line items"
              className="min-h-touch min-w-touch -mr-2 flex items-center justify-center text-amber-700 active:text-amber-800"
            >
              <Pencil className="h-4 w-4" />
            </Link>
          )}
        </div>
        <ul className="divide-y divide-gray-200">
          {(sale.line_items || []).map((li) => (
            <li key={li.id} className="py-2 first:pt-0 last:pb-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-body text-gray-900 truncate">
                    {li.product_name ?? `Product #${li.product_id}`}
                  </p>
                  <p className="text-caption text-gray-500">
                    {li.quantity} × {fmt(li.unit_price)}
                    {li.batch_id != null && (
                      <> · batch #{li.batch_id}</>
                    )}
                  </p>
                </div>
                <p className="text-body font-medium text-gray-900 whitespace-nowrap">
                  {fmt(li.total ?? li.quantity * li.unit_price)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Totals */}
      <section className="mb-card mb-3 space-y-1">
        {sale.subtotal != null && (
          <div className="flex justify-between text-small text-gray-600">
            <span>Subtotal (ex VAT)</span>
            <span>{fmt(sale.subtotal)}</span>
          </div>
        )}
        {Number(sale.shipping_amount) > 0 && (
          <div className="flex justify-between text-small text-gray-600">
            <span>Shipping</span>
            <span>{fmt(sale.shipping_amount)}</span>
          </div>
        )}
        {sale.vat_amount != null && (
          <div className="flex justify-between text-small text-gray-600">
            <span>VAT{sale.vat_rate ? ` (${(sale.vat_rate * 100).toFixed(1)}%)` : ''}</span>
            <span>{fmt(sale.vat_amount)}</span>
          </div>
        )}
        <div className="pt-2 mt-2 border-t border-gray-200 flex justify-between text-title font-semibold text-gray-900">
          <span>Total</span>
          <span>{fmt(sale.total_amount)}</span>
        </div>
        {Number(sale.outstanding_amount) > 0 && (
          <div className="flex justify-between text-small text-amber-700">
            <span>Outstanding</span>
            <span>{fmt(sale.outstanding_amount)}</span>
          </div>
        )}
      </section>

      {/* Notes */}
      {sale.notes && (
        <section className="mb-card mb-3">
          <p className="text-small font-medium text-gray-700 mb-1">Notes</p>
          <p className="text-small text-gray-700 whitespace-pre-wrap">{sale.notes}</p>
        </section>
      )}

      {/* Actions — PDFs → Release Note → Mark Delivered → Revert /
          Delete → flash/error. Edit-line-items lives inline as an icon
          on the Line items section header now (much lighter visual
          weight than a full action row). Actions hide once the sale's
          status moves them out of scope — backend mirrors the gating. */}
      <section className="space-y-2">
        {/* PDF rows — primary tap = view (auth-blob fetch + new tab),
            secondary tap on the share icon = native share sheet
            (WhatsApp / email / AirDrop). Share icon is only rendered
            when the browser supports file sharing — there's no point
            showing a button that does nothing on desktop browsers
            without the Web Share API. */}
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => openPdf('proforma')}
            disabled={pdfLoading === 'proforma'}
            className="mb-card-tap flex items-center gap-3 flex-1 text-left disabled:opacity-60"
          >
            <span className="w-10 h-10 rounded-card bg-blue-50 flex items-center justify-center shrink-0">
              {pdfLoading === 'proforma' ? (
                <Loader2 className="h-5 w-5 text-blue-700 animate-spin" />
              ) : (
                <FileText className="h-5 w-5 text-blue-700" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-gray-900">View proforma</p>
              <p className="text-caption text-gray-500">
                {pdfLoading === 'proforma' ? 'Generating PDF…' : 'PDF · opens in browser'}
              </p>
            </div>
            {!shareSupported && (
              <ExternalLink className="h-4 w-4 text-gray-400 shrink-0" />
            )}
          </button>
          {shareSupported && (
            <button
              type="button"
              onClick={() => onSharePdf('proforma')}
              disabled={pdfSharing === 'proforma'}
              aria-label="Share proforma"
              className="mb-card-tap min-w-touch flex items-center justify-center disabled:opacity-60"
            >
              {pdfSharing === 'proforma' ? (
                <Loader2 className="h-5 w-5 text-blue-700 animate-spin" />
              ) : (
                <Share2 className="h-5 w-5 text-blue-700" />
              )}
            </button>
          )}
        </div>

        {showInvoice && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => openPdf('invoice')}
              disabled={pdfLoading === 'invoice'}
              className="mb-card-tap flex items-center gap-3 flex-1 text-left disabled:opacity-60"
            >
              <span className="w-10 h-10 rounded-card bg-green-50 flex items-center justify-center shrink-0">
                {pdfLoading === 'invoice' ? (
                  <Loader2 className="h-5 w-5 text-green-700 animate-spin" />
                ) : (
                  <FileText className="h-5 w-5 text-green-700" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-body font-medium text-gray-900">View invoice</p>
                <p className="text-caption text-gray-500">
                  {pdfLoading === 'invoice' ? 'Generating PDF…' : 'PDF · opens in browser'}
                </p>
              </div>
              {!shareSupported && (
                <ExternalLink className="h-4 w-4 text-gray-400 shrink-0" />
              )}
            </button>
            {shareSupported && (
              <button
                type="button"
                onClick={() => onSharePdf('invoice')}
                disabled={pdfSharing === 'invoice'}
                aria-label="Share invoice"
                className="mb-card-tap min-w-touch flex items-center justify-center disabled:opacity-60"
              >
                {pdfSharing === 'invoice' ? (
                  <Loader2 className="h-5 w-5 text-green-700 animate-spin" />
                ) : (
                  <Share2 className="h-5 w-5 text-green-700" />
                )}
              </button>
            )}
          </div>
        )}

        {pdfError && (
          <p role="alert" className="text-small text-danger px-1">
            {pdfError}
          </p>
        )}

        {/* Release note row — three states:
              (1) loaded + exists  → Open RN link with status pill
              (2) loaded + none + sale is draft → Generate RN button
              (3) loaded + none + non-draft → render nothing (out of band)
              (undefined) still loading — render nothing to avoid layout flicker */}
        {rnSummary && (
          <Link
            to={`/release-notes/${rnSummary.id}`}
            className="mb-card-tap flex items-center gap-3 no-underline"
          >
            <span className="w-10 h-10 rounded-card bg-purple-50 flex items-center justify-center shrink-0">
              <ClipboardList className="h-5 w-5 text-purple-700" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-gray-900">
                Release note {rnSummary.release_number}
              </p>
              <p className="text-caption text-gray-500 capitalize">
                Status: {rnSummary.status?.replace(/_/g, ' ') ?? 'unknown'}
              </p>
            </div>
            <ExternalLink className="h-4 w-4 text-gray-400 shrink-0" />
          </Link>
        )}

        {canCreateRn && (
          <button
            type="button"
            onClick={onCreateRn}
            disabled={transitionInFlight}
            className="mb-card-tap flex items-center gap-3 w-full text-left disabled:opacity-60"
          >
            <span className="w-10 h-10 rounded-card bg-purple-50 flex items-center justify-center shrink-0">
              {transition === 'create_rn' ? (
                <Loader2 className="h-5 w-5 text-purple-700 animate-spin" />
              ) : (
                <ClipboardList className="h-5 w-5 text-purple-700" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-gray-900">Generate release note</p>
              <p className="text-caption text-gray-500">
                Starts the picking → authorise → collect workflow
              </p>
            </div>
          </button>
        )}

        {rnError && (
          <p role="alert" className="text-small text-danger px-1">
            {rnError}
          </p>
        )}

        {/* Mark as delivered — primary amber bar to signal it's the next
            step in the sale's lifecycle. Mirrors QuoteDetail's
            Convert-to-Sale styling. Hidden once status leaves draft. */}
        {canMarkDelivered && (
          <button
            type="button"
            onClick={onMarkDelivered}
            disabled={transitionInFlight}
            className="w-full flex items-center gap-3 rounded-card bg-amber-500 active:bg-amber-600 text-white shadow-lg p-4 disabled:opacity-60 active:scale-[0.99] transition-transform"
          >
            <span className="w-10 h-10 rounded-card bg-white/15 flex items-center justify-center shrink-0">
              {transition === 'deliver' ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <PackageCheck className="h-5 w-5" />
              )}
            </span>
            <div className="min-w-0 flex-1 text-left">
              <p className="text-body font-semibold">
                {transition === 'deliver' ? 'Marking delivered…' : 'Mark as delivered'}
              </p>
              <p className="text-caption text-white/80">
                {transition === 'deliver'
                  ? 'Deducting stock FIFO · posting ledger entry'
                  : 'Deducts stock FIFO · posts customer ledger entry'}
              </p>
            </div>
            <Truck className="h-5 w-5 text-white/80 shrink-0" />
          </button>
        )}

        {transitionFlash && (
          <p
            role="status"
            className="text-small text-green-800 bg-green-50 border border-green-200 rounded-card px-3 py-2"
          >
            {transitionFlash}
          </p>
        )}

        {transitionError && (
          <p role="alert" className="text-small text-danger px-1">
            {transitionError}
          </p>
        )}

        {/* Revert / Cancel — only on delivered or paid sales that
            haven't been fiscalised. Mirrors the desktop's row-action
            "Revert Sale" entry; opens the bottom-sheet form which
            captures action + reason + optional notes. Same gating
            logic as the backend so we don't render an action that
            would 400. Auth note: backend uses require_admin (not
            require_admin_or_sales like delete), so non-admin reps may
            still see this button and get a 403 on submit — the inline
            error in the sheet surfaces that. */}
        {canRevert && (
          <button
            type="button"
            onClick={() => setRevertOpen(true)}
            disabled={transitionInFlight}
            className="mb-card-tap flex items-center gap-3 w-full text-left disabled:opacity-60"
          >
            <span className="w-10 h-10 rounded-card bg-orange-50 flex items-center justify-center shrink-0">
              <Undo2 className="h-5 w-5 text-orange-700" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-body font-medium text-gray-900">Revert sale</p>
              <p className="text-caption text-gray-500">
                Send back to draft or cancel · stock + ledger restored
              </p>
            </div>
          </button>
        )}

        {showFiscalisedHint && (
          <p className="text-caption text-gray-500 px-1">
            This sale has been fiscalised. To unwind it, issue a Credit Note
            on the desktop ERP instead of reverting.
          </p>
        )}
        {/* Delete (draft only) lives in the PageHeader's right slot now —
            see the `right` prop above. Same handler, same confirm flow. */}
      </section>

      {/* Revert sheet — mounted only when open so its initial state
          (action toggle, reason, notes) resets cleanly each time the
          rep reopens it. On success we drop the updated SaleOut into
          local state so the page re-renders against the new status
          (draft / cancelled) and the action gates re-evaluate. */}
      {revertOpen && (
        <RevertSaleSheet
          sale={sale}
          onClose={() => setRevertOpen(false)}
          onSuccess={(updated) => {
            setSale(updated);
            setRevertOpen(false);
            setTransitionFlash(
              updated.status === 'cancelled'
                ? 'Sale cancelled · stock + ledger restored'
                : 'Sale reverted to draft · ready to edit again',
            );
          }}
        />
      )}
    </main>
  );
}
